import { Suspense, useEffect, useState } from 'react';
import React from 'react';
import { useSelector } from 'react-redux';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';

import { IState } from 'models/reducers/i-state.interface';

import { ROUTES } from 'constants/routes.constant';

import { InitializeReactGA } from 'services/analytics.service';
import { listenToViewportChanges } from 'services/viewport-height.service';

import GameOnboarding from './routes/gameOnboarding/GameOnboarding';
import GameOnboardingMobile from './routes/gameOnboardingMobile/GameOnboardingMobile';
import DailyRewards from 'routes/dailyRewards/DailyRewards';
import EventsRoute from 'routes/events/EventsRoute';
import Landing from 'routes/landing/Landing';
import MapRoute from 'routes/MapRoute';
import RedeemRoute from 'routes/redeem/RedeemRoute';
import AmaRoute from 'routes/referral/AmaRoute';
import InformationalRoute from 'routes/referral/InformationalRoute';
import ReferralRoute from 'routes/referral/ReferralRoute';
import TaskBasedRoute from 'routes/referral/TaskBasedRoute';
import SingleEventRoute from 'routes/singleEvent/SingleEventRoute';

import { CheckNetwork } from 'components/checkNetwork/CheckNetwork';
import CurrencyPurchaseWrapper from 'components/currencyPurchaseWrapper/CurrencyPurchaseWrapper';
import { DevDebugHeader } from 'components/devDebugHeader/DevDebugHeader';
import { DevRoyaltyMockSwitch } from 'components/devRoyaltyMockSwitch/DevRoyaltyMockSwitch';
import FullScreenLoadingSpinner from 'components/fullScreenLoadingSpinner/FullScreenLoadingSpinner';
import IconsSprite from 'components/iconsSprite/IconsSprite';
import { Login } from 'components/login/Login';
import Marquee from 'components/marquee/Marquee';
import Header from 'components/menu/header/Header';
import NotificationsPortal from 'components/notification/notificationsPortal/NotificationsPortal';
import SelectModal from 'components/selectModal/SelectModal';
// import TabsNavigation from 'components/tabsNavigation/TabsNavigation';
import WheelOfFortuneModal from 'components/wheelOfFortune/WheelOfFortuneModal/WheelOfFortuneModal';

import { useAccountChange } from 'hooks/useAccountChange';
import { useAppSettings } from 'hooks/useAppSettings';
import { useChainChange } from 'hooks/useChainChange';
import { useEnv } from 'hooks/useEnv';
import { useHomePageNavigate } from 'hooks/useHomePageNavigate';
import { useInitializeWeb3 } from 'hooks/useInitializeWeb3';
import { useMapRotation } from 'hooks/useMapRotation';
import { useReferrerWallet } from 'hooks/useReferrerWallet';
import { useWeb3ProviderChange } from 'hooks/useWeb3ProviderChange';

import { setDefaultBaseUrl } from 'utils/axios.utils';

import 'styles/App.scss';

const ContactRoute = React.lazy(() => import('routes/contact/ContactRoute'));
const GameRoute = React.lazy(() => import('routes/games/GameRoute'));
const GamesRoute = React.lazy(() => import('routes/games/GamesRoute'));
const PeriodicalStakingRoute = React.lazy(() => import('routes/periodicalStaking/PeriodicalStakingRoute'));
const MediaRoute = React.lazy(() => import('routes/media/MediaRoute'));
const PromotionsRoute = React.lazy(() => import('routes/promotions/PromotionsRoute'));
const StoreRoute = React.lazy(() => import('routes/store/StoreRoute'));
const PackageRoute = React.lazy(() => import('routes/package/PackageRoute'));
const StoreWrapper = React.lazy(() => import('routes/store/StoreWrapper'));
const WorldNftRoute = React.lazy(() => import('routes/worldNft/WorldNftRoute'));
const NftSwapRoute = React.lazy(() => import('routes/nftSwap/NftSwapRoute'));
const StakingV2Route = React.lazy(() => import('routes/staking_v2/StakingV2Route'));
const VipPage = React.lazy(() => import('routes/vip/VipPage'));
// DEV-ONLY: mock-data preview of the redesigned VIP page for visual QA — no wallet/backend/ToS
// gating. See src/routes/vip/preview/VipPreview.tsx. Its route is mounted only in the local
// dev environment (below); the MSW mock worker it relies on is likewise gated in vipPreviewMockApi.
const VipPreview = React.lazy(() => import('routes/vip/preview/VipPreview'));
const IS_LOCAL_ENVIRONMENT = process.env.REACT_APP_LOCAL_ENVIRONMENT === 'true';
const DashboardRoute = React.lazy(() => import('routes/dashboard/DashboardRoute'));

const App = () => {
  // web3
  useInitializeWeb3();
  useAccountChange();
  useChainChange();
  useWeb3ProviderChange();
  useReferrerWallet();

  // general
  const location = useLocation();

  const [appLoaded, setAppLoaded] = useState<boolean>(false);
  const [isGameOnboarding, setIsGameOnboarding] = useState<boolean>(false);
  const showWheelOfFortuneModal = useSelector((state: IState) => state.global.showCurrencyLotteryModal);
  const showCurrencyPurchaseModal = useSelector((state: IState) => state.global.showCurrencyPurchaseModal);
  const showDailyRewardsModal = useSelector((state: IState) => state.global.showDailyRewardsModal);
  const showSelectModal = useSelector((state: IState) => state.global.showSelectModal);

  useMapRotation();
  useAppSettings();

  const { redeemActive, hideLogin } = useEnv();
  const { homePage, navigateToHomePage } = useHomePageNavigate();

  // useEffect(() => {
  //   const preventDefault = (event: Event) => event.preventDefault();

  //   document.addEventListener('contextmenu', preventDefault);

  //   return () => {
  //     document.removeEventListener('contextmenu', preventDefault);
  //   };
  // });

  useEffect(() => {
    setDefaultBaseUrl();
    listenToViewportChanges();
    InitializeReactGA();
    // Initialization of Google Tag Manager has been commented out because all of the custom events were only sent to GTM and no events got to GA
    // This should be fixed to make sure that events are routed to both preferably, or custom events should be routed to GA only while GTM is initialized
    //InitializeReactGTM();
    setAppLoaded(true);

    if (location.pathname === ROUTES.HOME || location.pathname === `${ROUTES.HOME}/`) {
      navigateToHomePage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const pathname = `/${location.pathname.split('/')[1]}`;
    setIsGameOnboarding(['/game-onboarding', '/game-onboarding-mobile'].includes(pathname));
  }, [location.pathname]);

  return (
    <Suspense fallback={<FullScreenLoadingSpinner />}>
      <div className="app">
        <DevDebugHeader />
        <DevRoyaltyMockSwitch />
        <Marquee
          text="Swap your T0 Digital Assets before November 5, 23:59 UTC to stay eligible for the full World NFT airdrop series — starting with the US NFT!"
          link="https://medium.com/@Realitymeta/world-nft-snapshot-deadline-extended-dont-miss-out-on-the-top-15-countries-1899d9db7fe6"
          space={115}
          visibleUntil={new Date('2025-11-05T23:59:59Z').getTime()}
        />
        <NotificationsPortal />
        {!hideLogin && <Login />}
        {showCurrencyPurchaseModal && <CurrencyPurchaseWrapper />}
        {showWheelOfFortuneModal && <WheelOfFortuneModal />}
        {showSelectModal && <SelectModal />}
        {showDailyRewardsModal && <DailyRewards />}
        <IconsSprite />
        {appLoaded && (
          <>
            <CheckNetwork>
              {isGameOnboarding ? (
                <></>
              ) : (
                <header className="app-header">
                  <Header />
                  {/* <TabsNavigation currentPath={location.pathname} /> */}
                </header>
              )}
              <Routes>
                <Route
                  path={`${ROUTES.REDEEM}`}
                  element={redeemActive ? <RedeemRoute /> : <Navigate to={homePage} />}
                />
                <Route
                  path="/"
                  element={
                    <StoreWrapper>
                      <Landing />
                    </StoreWrapper>
                  }
                />
                <Route path={`${ROUTES.GAMES}/:game`} element={<GameRoute />} />
                <Route path={ROUTES.GAMES} element={<GamesRoute />} />
                <Route path={ROUTES.STAKING_OUTDATED} element={<PeriodicalStakingRoute />} />
                <Route path={ROUTES.PERIODICAL_STAKING} element={<StakingV2Route />} />
                {/* VIP rewards is intentionally NOT wrapped in <IsLoggedIn>: not-connected users
                    must see the rich teaser view (the page renders a "Connect wallet" CTA itself). */}
                <Route path={ROUTES.VIP} element={<VipPage />} />
                {IS_LOCAL_ENVIRONMENT && <Route path="/vip-preview" element={<VipPreview />} />}
                <Route path={ROUTES.DASHBOARD} element={<DashboardRoute />} />
                <Route path={ROUTES.MEDIA} element={<MediaRoute />} />
                <Route path={ROUTES.CONTACT} element={<ContactRoute />} />
                <Route path={ROUTES.PROMOTIONS} element={<PromotionsRoute />} />
                <Route
                  path={ROUTES.STORE}
                  element={
                    <StoreWrapper>
                      <StoreRoute />
                    </StoreWrapper>
                  }
                />
                <Route
                  path={`${ROUTES.PACKAGE}/:id`}
                  element={
                    <StoreWrapper>
                      <PackageRoute />
                    </StoreWrapper>
                  }
                />
                <Route
                  path={`${ROUTES.WORLD_NFT}`}
                  element={
                    <StoreWrapper>
                      <WorldNftRoute />
                    </StoreWrapper>
                  }
                />
                <Route path={`${ROUTES.NFT_SWAP}`} element={<NftSwapRoute />} />
                <Route path={`${ROUTES.HOME}/*`} element={<MapRoute />} />
                <Route path="*" element={<Navigate to={homePage} />} />
                <Route path="/game-onboarding" element={<GameOnboarding />} />
                <Route path="/game-onboarding-mobile" element={<GameOnboardingMobile />} />
                <Route path={`${ROUTES.EVENTS}`} element={<EventsRoute />} />
                <Route path={`${ROUTES.TRADING_EVENT}/:id`} element={<SingleEventRoute />} />
                <Route path={`${ROUTES.REFERRAL}/:id`} element={<ReferralRoute />} />
                <Route path={`${ROUTES.AMA}/:id`} element={<AmaRoute />} />
                <Route path={`${ROUTES.INFORMATIONAL}/:id`} element={<InformationalRoute />} />
                <Route path={`${ROUTES.TASK_BASED}/:id`} element={<TaskBasedRoute />} />
              </Routes>
            </CheckNetwork>
          </>
        )}
      </div>
      <div className="modal-container"></div>
    </Suspense>
  );
};

export default App;
